import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, FolderOpen, Edit2, Trash2, Folder } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { CreateProjectDialog } from './CreateProjectDialog';
import { Badge } from '@/components/ui/badge';

interface CsvProject {
  id: string;
  name: string;
  description?: string;
  color?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  file_count?: number;
}

interface CsvProjectManagerProps {
  selectedProjectId: string | null;
  onProjectSelect: (projectId: string | null) => void;
  className?: string;
}

export const CsvProjectManager: React.FC<CsvProjectManagerProps> = ({
  selectedProjectId,
  onProjectSelect,
  className = ''
}) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<CsvProject | null>(null);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch user's projects
  const { data: projects = [] } = useQuery({
    queryKey: ['csv-projects', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('csv_projects')
        .select(`
          *,
          user_csv_uploads!user_csv_uploads_project_id_fkey(count)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      return data.map(project => ({
        ...project,
        file_count: project.user_csv_uploads?.[0]?.count || 0
      })) as CsvProject[];
    },
    enabled: !!user?.id,
  });

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      const { error } = await supabase
        .from('csv_projects')
        .delete()
        .eq('id', projectId)
        .eq('user_id', user?.id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['csv-projects'] });
      queryClient.invalidateQueries({ queryKey: ['csv-uploads'] });
      toast.success('Project deleted successfully');
      
      // If deleted project was selected, switch to "All Files"
      if (selectedProjectId && projects.find(p => p.id === selectedProjectId)) {
        onProjectSelect(null);
      }
    },
    onError: (error) => {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    },
  });

  const handleDeleteProject = (project: CsvProject) => {
    if (project.file_count && project.file_count > 0) {
      toast.error('Cannot delete project with files. Move files to other projects first.');
      return;
    }
    
    if (confirm(`Are you sure you want to delete "${project.name}"? This action cannot be undone.`)) {
      deleteProjectMutation.mutate(project.id);
    }
  };

  const getProjectColor = (color?: string) => {
    return color || '#3B82F6';
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Folder className="h-5 w-5" />
            Projects
          </CardTitle>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            size="sm"
            className="h-8 w-8 p-0"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="space-y-2">
          {/* All Files Option */}
          <Button
            variant={selectedProjectId === null ? "secondary" : "ghost"}
            className="w-full justify-start h-auto p-3"
            onClick={() => onProjectSelect(null)}
          >
            <div className="flex items-center gap-3 w-full">
              <div className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4" />
                <span className="font-medium">All Files</span>
              </div>
            </div>
          </Button>

          {/* Project List */}
          {projects.map((project) => (
            <div
              key={project.id}
              className={`relative group border rounded-lg p-3 transition-all ${
                selectedProjectId === project.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50'
              }`}
            >
              <Button
                variant="ghost"
                className="w-full justify-start h-auto p-0 hover:bg-transparent"
                onClick={() => onProjectSelect(project.id)}
              >
                <div className="flex items-center gap-3 w-full">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: getProjectColor(project.color) }}
                  />
                  <div className="flex-1 text-left">
                    <div className="font-medium">{project.name}</div>
                    {project.description && (
                      <div className="text-sm text-muted-foreground mt-0.5">
                        {project.description}
                      </div>
                    )}
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {project.file_count || 0}
                  </Badge>
                </div>
              </Button>

              {/* Project Actions */}
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingProject(project);
                      setIsCreateDialogOpen(true);
                    }}
                  >
                    <Edit2 className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProject(project);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <CreateProjectDialog
        isOpen={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setEditingProject(null);
        }}
        editingProject={editingProject}
      />
    </Card>
  );
};